import * as React from "react";
import { Menu } from "semantic-ui-react";
import { URLS, useAccountSettings } from "@/utils";
import { PageWrapper } from "@/components/PageWrapper";

import PromptsTab from "./PromptsTab";
import ScriptsTab from "./ScriptsTab";
import VideoProjectsTab from "./VideoProjectsTab";
import VideoPagesTab from "./VideoPagesTab";

import { Redirect, useHistory, useParams } from "react-router-dom";
import { kebabCase } from "lodash";
import { WebComponents, WebSettings } from "@/interfaces";
import { SmartLinksTab } from "@/containers/Coach/Library/SmartLinksTab";
import { BlogPage, BlogsList } from "@/containers/Blogs";

const BlogsTab: React.FC<{ id?: string }> = ({ id }) => (id !== undefined ? <BlogPage /> : <BlogsList />);

const LibraryFC: React.FC = () => {
  const history = useHistory();
  type Tab = { title: string; keys: string[]; component: any };
  const tabs: Tab[] = [];
  const { tab, id } = useParams<{ tab: string; id?: string }>();
  const { hasReadPermission, hasAccountSetting } = useAccountSettings();

  const permissionsAndTabs = new Map<WebComponents, Tab>([
    [WebComponents.PROMPTS, { title: "Prompts", keys: ["prompts"], component: PromptsTab }],
    [WebComponents.MANAGE_SCRIPT_LIBRARY, { title: "Scripts", keys: ["scripts"], component: ScriptsTab }],
    [WebComponents.VIDEO_CATALYST, { title: "Video Catalyst", keys: ["video-projects"], component: VideoProjectsTab }],
    [
      WebComponents.VIDEO_PAGES,
      { title: "Video Pages", keys: ["video-pages", "super-pages"], component: VideoPagesTab },
    ],
  ]);
  permissionsAndTabs.forEach((value, key) => {
    if (hasReadPermission(key, value.keys.includes(tab))) {
      tabs.push(value);
    }
  });
  if (hasReadPermission(WebComponents.PROMPTS) && hasAccountSetting(WebSettings.LINK_TRACKING)) {
    tabs.push({ title: "Smart Links", keys: ["smart-links"], component: SmartLinksTab });
  }
  if (hasReadPermission(WebComponents.VIDEO_PAGES) && hasAccountSetting(WebSettings.BLOGS)) {
    tabs.push({ title: "Blogs", keys: ["blogs"], component: BlogsTab });
  }

  const matchingTab = tabs.find(t => t.keys.includes(tab));
  const defaultTabKey = tabs[0]?.keys[0];

  const selectTab = (newTab: string) => {
    history.replace(URLS.coach.libraryTab.replace(":tab?", kebabCase(newTab)));
  };

  if (!matchingTab && defaultTabKey) {
    return <Redirect to={URLS.coach.libraryTab.replace(":tab?", kebabCase(defaultTabKey))} />;
  }

  const TabComponent = matchingTab?.component ?? tabs[0]?.component;
  const showMenu = id === undefined && tab !== "super-pages";

  return (
    <PageWrapper title={showMenu ? "Library" : undefined} hideSeparator className="max-w-325">
      {showMenu && (
        <Menu pointing secondary className="mt-0">
          {tabs.map((t, index) => {
            return (
              <Menu.Item
                key={`tab-${index}`}
                name={t.title}
                active={t.keys.includes(tab)}
                onClick={() => selectTab(t.keys[0])}
              />
            );
          })}
        </Menu>
      )}
      {TabComponent && <TabComponent tab={tab} id={id} />}
    </PageWrapper>
  );
};

export const Library = LibraryFC;
